/**
 * Admin Strategy Scoreboard Page (2026-06-02 / L3 görselleştirme).
 *
 * Veri kaynağı: GET /api/admin/strategy-performance?days=14
 * + POST /api/admin/strategy/toggle (aç/kapat)
 *
 * Amaç: 14 günlük shadow + gerçek trade aggregate'i üzerinden stratejileri
 * yüksek/orta/düşük skor olarak renklendir, tek-tık deaktive seçeneği sun.
 * 14 gün sonra otomatik karar mekaniğinin görsel önizlemesi.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PERIOD_OPTIONS = [
  { value: 7, label: "7 Gün" },
  { value: 14, label: "14 Gün (önerilen)" },
  { value: 30, label: "30 Gün" },
  { value: 60, label: "60 Gün" },
  { value: 90, label: "90 Gün" },
];

/**
 * Composite skor:
 *   • trade_count > 0 ise: win_rate ağırlık 50%, normalize avg_pnl 30%, shadow_score 20%
 *   • trade_count == 0 (sadece shadow): shadow_would_have_traded_rate 60%, avg_score 40%
 * Output: 0..1
 */
function computeCompositeScore(strategy) {
  const tc = Number(strategy.trade_count || 0);
  if (tc > 0) {
    const winRateNorm = Number(strategy.win_rate || 0);
    const avgPnl = Number(strategy.avg_realized_pnl || 0);
    const pnlNorm = Math.max(0, Math.min(1, (avgPnl + 50) / 100)); // -50..+50 → 0..1
    const shadowScore = Math.max(0, Math.min(1, (Number(strategy.shadow_avg_score || 0) + 1) / 2));
    return Number((0.5 * winRateNorm + 0.3 * pnlNorm + 0.2 * shadowScore).toFixed(3));
  }
  const wouldRate = Number(strategy.shadow_would_have_traded_rate || 0);
  const shadowScore = Math.max(0, Math.min(1, (Number(strategy.shadow_avg_score || 0) + 1) / 2));
  return Number((0.6 * wouldRate * 5 + 0.4 * shadowScore).toFixed(3));
}

function tierFromScore(score) {
  if (score >= 0.6) return { label: "YÜKSEK", classes: "border-emerald-500/60 bg-emerald-500/10 text-emerald-100" };
  if (score >= 0.4) return { label: "ORTA", classes: "border-amber-500/60 bg-amber-500/10 text-amber-100" };
  return { label: "DÜŞÜK", classes: "border-rose-500/60 bg-rose-500/10 text-rose-100" };
}

export const AdminStrategyScoreboardPage = () => {
  const [days, setDays] = useState(14);
  const [performance, setPerformance] = useState(null);
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [perfRes, toggleRes] = await Promise.allSettled([
        apiClient.get(`/admin/strategy-performance?days=${days}`),
        apiClient.get(`/admin/strategy/toggle`),
      ]);
      if (perfRes.status === "fulfilled") {
        setPerformance(perfRes.value?.data || null);
      } else {
        toast.error("Performance verisi yüklenemedi");
      }
      if (toggleRes.status === "fulfilled") {
        const items = toggleRes.value?.data?.items || [];
        const map = {};
        for (const it of items) {
          map[it.strategy_id] = it;
        }
        setToggles(map);
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleToggle = async (strategyId, currentEnabled) => {
    setTogglingId(strategyId);
    try {
      await apiClient.post("/admin/strategy/toggle", {
        strategy_id: strategyId,
        enabled: !currentEnabled,
      });
      toast.success(`${strategyId} ${!currentEnabled ? "aktif" : "deaktif"} edildi`);
      await fetchAll();
    } catch (e) {
      // 2026-06-02 fix-2: Registry mismatch handle.
      // L3 aggregator user_trade_projections.strategy_name'i toplar — bu
      // bazen code-side registry'de olmayan eski/deprecated ID olabilir.
      // Toggle endpoint "unknown_strategy_id" döner. Kullanıcıya net mesaj ver.
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("unknown_strategy_id")) {
        toast.error(`${strategyId} code-registry'de yok (deprecated/legacy ID). Toggle uygulanamadı.`);
      } else {
        toast.error(detail || "Toggle başarısız");
      }
    } finally {
      setTogglingId("");
    }
  };

  const rows = useMemo(() => {
    const items = Array.isArray(performance?.strategies) ? performance.strategies : [];
    return items.map((s) => {
      const score = computeCompositeScore(s);
      const tier = tierFromScore(score);
      const toggle = toggles[s.strategy_id];
      const inCodeRegistry = Boolean(toggle);
      return {
        ...s,
        composite_score: score,
        tier_label: tier.label,
        tier_classes: tier.classes,
        is_enabled: toggle ? Boolean(toggle.enabled) : true,
        role: toggle?.role || "—",
        in_code_registry: inCodeRegistry,
      };
    });
  }, [performance, toggles]);

  const summary = performance?.summary || { total_trades: 0, net_pnl: 0, win_rate: 0, strategies_tracked: 0 };

  return (
    <section className="space-y-4" data-testid="admin-strategy-scoreboard-page">
      <header className="border border-slate-800 bg-slate-900 p-4">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="scoreboard-title">
          Strategy Scoreboard (L3)
        </h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="scoreboard-subtitle">
          Shadow + gerçek trade aggregate'i. 14 gün sonra otomatik karar mekaniği için ön-görselleştirme.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 border border-slate-800 bg-slate-900 p-3" data-testid="scoreboard-controls">
        <label htmlFor="period-select" className="text-xs uppercase tracking-wide text-slate-300">Periyot</label>
        <select
          id="period-select"
          className="border border-slate-700 bg-slate-800 px-3 py-1 text-sm text-slate-100"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="scoreboard-period-select"
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <Button
          size="sm"
          variant="outline"
          className="border-cyan-400 bg-transparent text-cyan-200"
          onClick={fetchAll}
          disabled={loading}
          data-testid="scoreboard-refresh-btn"
        >
          {loading ? "Yükleniyor…" : "Yenile"}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="scoreboard-summary">
        <SummaryCard label="Toplam Trade" value={summary.total_trades} testid="scoreboard-summary-trades" />
        <SummaryCard label="Net PnL (USDT)" value={Number(summary.net_pnl || 0).toFixed(2)} testid="scoreboard-summary-pnl" tone={summary.net_pnl >= 0 ? "pos" : "neg"} />
        <SummaryCard label="Win Rate" value={`${(Number(summary.win_rate || 0) * 100).toFixed(1)}%`} testid="scoreboard-summary-winrate" />
        <SummaryCard label="İzlenen Strateji" value={summary.strategies_tracked} testid="scoreboard-summary-tracked" />
      </div>

      <div className="overflow-x-auto border border-slate-800 bg-slate-900" data-testid="scoreboard-table-wrapper">
        <table className="min-w-full text-sm" data-testid="scoreboard-table">
          <thead className="border-b border-slate-700 bg-slate-950 text-xs uppercase tracking-wide text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">Strateji</th>
              <th className="px-3 py-2 text-left">Rol</th>
              <th className="px-3 py-2 text-right">Trade</th>
              <th className="px-3 py-2 text-right">Win Rate</th>
              <th className="px-3 py-2 text-right">Toplam PnL</th>
              <th className="px-3 py-2 text-right">Avg PnL</th>
              <th className="px-3 py-2 text-right">Shadow Obs</th>
              <th className="px-3 py-2 text-right">Would-Trade</th>
              <th className="px-3 py-2 text-right">Skor</th>
              <th className="px-3 py-2 text-center">Tier</th>
              <th className="px-3 py-2 text-center">Durum</th>
              <th className="px-3 py-2 text-center">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr data-testid="scoreboard-empty-row">
                <td colSpan={12} className="px-3 py-6 text-center text-slate-500">
                  {loading ? "Yükleniyor…" : "Bu periyot için veri yok. Bot bir süre çalıştırıldıktan sonra tekrar deneyin."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.strategy_id} className="border-b border-slate-800 hover:bg-slate-800/50" data-testid={`scoreboard-row-${r.strategy_id}`}>
                <td className="px-3 py-2 font-mono text-xs" data-testid={`scoreboard-cell-id-${r.strategy_id}`}>{r.strategy_id}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{r.role}</td>
                <td className="px-3 py-2 text-right">{r.trade_count}</td>
                <td className="px-3 py-2 text-right">{(Number(r.win_rate || 0) * 100).toFixed(1)}%</td>
                <td className={`px-3 py-2 text-right font-semibold ${Number(r.total_realized_pnl) > 0 ? "text-emerald-300" : Number(r.total_realized_pnl) < 0 ? "text-rose-300" : "text-slate-300"}`}>
                  {Number(r.total_realized_pnl || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right">{Number(r.avg_realized_pnl || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-slate-400">{r.shadow_observations}</td>
                <td className="px-3 py-2 text-right">{(Number(r.shadow_would_have_traded_rate || 0) * 100).toFixed(2)}%</td>
                <td className="px-3 py-2 text-right font-bold" data-testid={`scoreboard-cell-score-${r.strategy_id}`}>{r.composite_score.toFixed(3)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block rounded border px-2 py-0.5 text-xs font-bold ${r.tier_classes}`} data-testid={`scoreboard-cell-tier-${r.strategy_id}`}>
                    {r.tier_label}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  <span className={r.is_enabled ? "text-emerald-300" : "text-slate-500"} data-testid={`scoreboard-cell-status-${r.strategy_id}`}>
                    {r.is_enabled ? "Aktif" : "Deaktif"}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.in_code_registry ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className={r.is_enabled ? "border-rose-400 bg-transparent text-rose-200" : "border-emerald-400 bg-transparent text-emerald-200"}
                      onClick={() => handleToggle(r.strategy_id, r.is_enabled)}
                      disabled={togglingId === r.strategy_id}
                      data-testid={`scoreboard-toggle-btn-${r.strategy_id}`}
                    >
                      {togglingId === r.strategy_id ? "…" : r.is_enabled ? "Deaktive Et" : "Aktive Et"}
                    </Button>
                  ) : (
                    <span
                      className="inline-block rounded border border-slate-600 bg-slate-800 px-2 py-1 text-[10px] text-slate-400"
                      data-testid={`scoreboard-no-registry-${r.strategy_id}`}
                      title="Bu strategy_id code-side registry'de tanımlı değil (deprecated/legacy). Toggle uygulanamaz; sadece geçmiş veri görünür."
                    >
                      legacy/deprecated
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-800 bg-slate-900 p-3 text-xs text-slate-400" data-testid="scoreboard-legend">
        <p className="font-semibold text-slate-200">Skor Hesaplama</p>
        <p className="mt-1">
          <span className="text-emerald-300">YÜKSEK (≥0.6)</span> · <span className="text-amber-300">ORTA (0.4–0.6)</span> · <span className="text-rose-300">DÜŞÜK (&lt;0.4)</span>
        </p>
        <p className="mt-1">
          <strong>Trade varsa:</strong> 0.5·win_rate + 0.3·norm(avg_pnl) + 0.2·shadow_score &nbsp;|&nbsp;
          <strong>Sadece shadow:</strong> 0.6·(would_have_traded_rate·5) + 0.4·shadow_score
        </p>
        <p className="mt-1 italic text-slate-500">
          14 gün sonra otomatik decision (örn: 3 ardışık periyotta tier=DÜŞÜK ise auto-deaktive) bu skorları input olarak alacak.
        </p>
      </div>
    </section>
  );
};

function SummaryCard({ label, value, testid, tone }) {
  const toneClasses =
    tone === "pos" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
    : tone === "neg" ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
    : "border-slate-700 bg-slate-800 text-slate-100";
  return (
    <div className={`rounded border p-3 ${toneClasses}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wide opacity-70" data-testid={`${testid}-label`}>{label}</p>
      <p className="text-2xl font-black" data-testid={`${testid}-value`}>{value}</p>
    </div>
  );
}

export default AdminStrategyScoreboardPage;
